import { EVENT_TYPE } from '@atlaskit/editor-common/analytics';
import type { Schema } from '@atlaskit/editor-prosemirror/model';
import { Fragment } from '@atlaskit/editor-prosemirror/model';
import { fg } from '@atlaskit/platform-feature-flags';
import {
	convertMarkdownToProsemirror,
	type AnalyticsAttributes,
} from '@atlassian/ai-model-io/convert-markdown-to-prosemirror';
import type { IDMap } from '@atlassian/ai-model-io/convert-prosemirror-to-markdown/serializer';
import { ErrorUtils } from '@atlassian/editor-ai-common/utils/errors';

import type { FailedSource } from '../../types/analytics';

import type { useFireAIAnalyticsEvent } from './analytics/utils';

type PmFragmentPreviewData = {
	isTextFallbackUsed: boolean;
	pmFragment: Fragment;
} & AnalyticsAttributes;

export const enableComplexNodes = () => {
	if (!fg('platform_editor_ai_markdown_plus_add_complex_nodes')) {
		return false;
	}

	if (!fg('platform_editor_ai_proactive_ai_nudge')) {
		return false;
	}

	return true;
};

export function getPMFragmentWithFallback({
	markdown,
	schema,
	source,
	fireAIAnalyticsEvent,
	idMap,
}: {
	fireAIAnalyticsEvent: ReturnType<typeof useFireAIAnalyticsEvent>;
	idMap: IDMap;
	markdown: string;
	schema: Schema;
	source: FailedSource;
}): PmFragmentPreviewData {
	try {
		const {
			pmFragment,
			htmlConvertedToFallbackNodes,
			htmlConvertedToNodes,
			unclosedTokens,
			markdownPlusAttributeStatus,
		} = convertMarkdownToProsemirror({
			schema,
			markdown,
			idMap,
			featureToggles: {
				markdownPlus: true,
				markdownPlusExtensions: true,
				markdownPlusPanels: true,
				markdownPlusDecisions: true,
				markdownPlusAttributeTest: fg('platform_editor_ai_markdown_plus_attr_test'),
				markdownPlusMentions: fg('platform_editor_ai_draft_reply_mentions'),
				markdownPlusAddComplexNodes: enableComplexNodes(),
				markdownPlusRemoveUnclosedTokens: fg('platform_editor_ai_remove_unclosed_tokens'),
				markdownPlusUseNextMediaPlugin: fg('platform_editor_ai_next_media_plugin'),
				allowAttributesMatchDefault: fg('platform_editor_ai_attributes_match_default'),
			},
		});
		if (
			markdownPlusAttributeStatus.length > 0 &&
			fg('platform_editor_ai_markdown_plus_attr_test')
		) {
			markdownPlusAttributeStatus.forEach((status) => {
				fireAIAnalyticsEvent({
					payload: {
						action: 'validateAttributes',
						actionSubject: 'editorPluginAI',
						actionSubjectId: 'markdownPlusAttributes',
						attributes: {
							attributesMatch: status[1],
							nodeType: status[0],
						},
						eventType: EVENT_TYPE.OPERATIONAL,
					},
				});
			});
		}

		return {
			pmFragment,
			unclosedTokens,
			htmlConvertedToFallbackNodes,
			htmlConvertedToNodes,
			isTextFallbackUsed: false,
		};
	} catch (error) {
		// When convertMarkdownToProsemirror a fragment
		// we log an analytics event to call out that markdown conversion has failed
		fireAIAnalyticsEvent({
			payload: {
				action: 'failed',
				actionSubject: 'editorPluginAI',
				actionSubjectId: 'markdownParser',
				attributes: {
					source,
					errorType: 'markdownParser',
					errorMessage: ErrorUtils.extractErrorMessage(error),
				},
				eventType: EVENT_TYPE.OPERATIONAL,
			},
		});

		// Instead of erroring, we fallback to rendering as plaintext in a single paragraph.
		const fallbackPMFragment = Fragment.from(
			schema.nodes.paragraph.create(null, schema.text(markdown)),
		);
		return {
			pmFragment: fallbackPMFragment,
			unclosedTokens: {},
			htmlConvertedToFallbackNodes: 0,
			htmlConvertedToNodes: 0,
			isTextFallbackUsed: true,
		};
	}
}
